import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Button, Card, CardBody, Table, Badge } from "reactstrap";
import StatusModal from "./StatusModal";
import OrdersModal from "./OrdersModal";
import { updateOrderStatusRequest } from "../../store/orderManagement/actions";
import { formatDistanceToNow, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';

const filterOrderList = (orders, filterValue) => {
  if (filterValue === "") {
    return orders;
  }
  if (filterValue === "delayed") {
    return orders.filter(isOrderDelayed);
  }
  return orders.filter(order => order.booking_status === filterValue);
};

// Helper: pick newest timestamp (updatedAt preferred, else create_at)
const parseDate = (ts) => {
  if (!ts) return 0;
  if (ts.seconds) return new Date(ts.seconds * 1000).getTime();
  if (typeof ts === 'string') return new Date(ts).getTime();
  return new Date(ts).getTime();
};

const getBookingExecuteDate = (bookingExecuteDate) => {
  if (!bookingExecuteDate) return null;

  if (bookingExecuteDate?.seconds) {
    return new Date(bookingExecuteDate.seconds * 1000);
  }

  if (typeof bookingExecuteDate === "string") {
    const parsed = new Date(bookingExecuteDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(bookingExecuteDate);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const hasDelayLock = (order) => Boolean(order?.delay_locked);

const isOrderLiveDelayed = (order) => {
  const bookingStatus = String(order?.booking_status || "").toLowerCase();
  const paymentStatus = String(order?.paymentStatus || "").toLowerCase();
  const executeDate = getBookingExecuteDate(order?.booking_execute_date);

  if (!executeDate) return false;

  const eligibleStatus = bookingStatus === "pending" || bookingStatus === "approved";
  const isPaid = paymentStatus === "paid";

  return eligibleStatus && isPaid && executeDate.getTime() < Date.now();
};

const isOrderDelayed = (order) => {
  return hasDelayLock(order) || isOrderLiveDelayed(order);
};

const OrderManagementDashboard = ({ orders }) => {
  const [orderList, setOrderList] = useState(orders);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [modal1, setModal1] = useState(false);
  const [selectedOrderData, setSelectedOrderData] = useState(null);
  const [filterValue, setFilterValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isServiceDeleted, setIsServiceDeleted] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    let filteredOrders = filterOrderList(orders, filterValue);

    if (searchQuery) {
      filteredOrders = filteredOrders.filter(order => {
        const user = order.app_user_id || {};
        const service = order.serviceProviderServices || {};
        const query = searchQuery.toLowerCase();
        return (
          order.id?.toLowerCase().includes(query) ||
          user.username?.toLowerCase().includes(query) ||
          service.name?.toLowerCase().includes(query) ||
          user.mobile?.includes(searchQuery)
        );
      });
    }

    // Sort by newest (updatedAt fallback to create_at)
    filteredOrders.sort((a, b) => {
      const da = parseDate(a.updatedAt) || parseDate(a.create_at);
      const db = parseDate(b.updatedAt) || parseDate(b.create_at);
      return db - da;
    });

    setOrderList(filteredOrders);
  }, [orders, filterValue, searchQuery]);

  // Format Firebase timestamp
  const formatFirebaseTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp.seconds * 1000);
    return formatDistanceToNow(date, { addSuffix: true, includeSeconds: true, locale: ar });
  };
  const toggleViewStatusModal = (orderId, status, order) => {
    setSelectedOrderId(orderId);
    setSelectedStatus(status);
    setSelectedOrderData(order);
    // Check if the service referenced in the order is soft-deleted
    const serviceIsDeleted = order?.serviceProviderServices?.is_deleted === true;
    setIsServiceDeleted(serviceIsDeleted);
    setStatusModalOpen(!statusModalOpen);
  };

  const toggleViewModal = (orderData) => {
    setSelectedOrderData(orderData);
    setModal1(!modal1);
  };

  const toggleStatusModal = () => {
    setStatusModalOpen(!statusModalOpen);
  };
  const updateOrderStatus = (orderId, newStatus) => {
    const updatedOrderList = orderList.map((order) =>
      order.$id === orderId ? { ...order, booking_status: newStatus } : order
    );
    setOrderList(updatedOrderList);
    dispatch(updateOrderStatusRequest(orderId, newStatus));
    toggleStatusModal();
  };
  return (
    <React.Fragment>
      <h2>لوحة إدارة الطلبات</h2>

      <div className="mb-4 mt-4">
        <label>البحث:</label>
        <input
          type="text"
          className="form-control"
          placeholder="ابحث عن الطلبات... مثل رقم الهاتف، الاسم، اسم الخدمة، إلخ"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      <div>
        <FilterOptions filterValue={filterValue} setFilterValue={setFilterValue} />
      </div>
 
      <Card>
        <CardBody>
          {orderList.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted">لا توجد طلبات مطابقة حاليًا</p>
              <Button color="secondary" onClick={() => { setFilterValue(""); setSearchQuery(""); }}>
                مسح الفلاتر
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table className="table-striped">
                <thead>
                  <tr>
                    <th>التاريخ</th>
                    <th>العميل</th>
                    <th>مقدم الخدمة</th>
                    <th>الخدمة</th>
                    <th>الهاتف</th>
                    <th>الدفع</th>
                    <th>الحالة</th>
                    <th>التأخير</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {orderList.map(order => {
                    const user = order.app_user_id || {};
                    const provider = order.service_providers_id || {};
                    const service = order.serviceProviderServices || {};
                    const delayed = isOrderDelayed(order);
                    const liveDelayed = isOrderLiveDelayed(order);
                    const stickyDelayed = hasDelayLock(order) && !liveDelayed;
                    const executeDate = getBookingExecuteDate(order.booking_execute_date);
                    
                      return (
                      <tr key={order.id} className={delayed ? "table-danger" : ""}>
                        <td>{formatFirebaseTimestamp(order.create_at)}</td>
                        <td>{user.username || 'N/A'}</td>
                        <td>{provider.full_name || 'N/A'}</td>
                        <td>{service.name || 'N/A'}</td>
                        <td style={{ direction: "ltr" }}>{user.mobile || 'N/A'}</td>
                        <td>
                          <Badge color={getPaymentStatusColor(order.paymentStatus)}>
                            {translatePaymentStatus(order.paymentStatus)}
                          </Badge>
                        </td>
                        <td>
                        <span className={`badge bg-${getStatusBadgeColor(order.booking_status.toLowerCase())}`}>
                          {translateStatus(order.booking_status.toLowerCase())}
                        </span>
                        </td>
                        <td>
                          {delayed ? (
                            <div className="d-flex flex-column gap-1">
                              <Badge color="danger">{stickyDelayed ? "متأخر (مثبت)" : "متأخر"}</Badge>
                              {executeDate && (
                                <small className="text-danger fw-semibold">
                                  {formatDistanceToNow(executeDate, { addSuffix: true, locale: ar })}
                                </small>
                              )}
                            </div>
                          ) : (
                            <Badge color="light" className="text-dark">في الموعد</Badge>
                          )}
                        </td>
                        <td>
                        <div className="d-flex gap-2">
                          <Button
                          color="primary"
                          size="sm"
                          onClick={() => toggleViewModal(order)}
                          >
                          التفاصيل
                          </Button>
                          <Button
                          color="warning"
                          size="sm"
                          onClick={() => toggleViewStatusModal(order.id, order.booking_status.toLowerCase(), order)}
                          >
                          تغيير الحالة
                          </Button>
                        </div>
                        </td>
                      </tr>
                      );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <StatusModal
        isOpen={statusModalOpen}
        toggle={() => setStatusModalOpen(!statusModalOpen)}
        orderId={selectedOrderId}
        status={selectedStatus}
        orderData={selectedOrderData}
        isServiceDeleted={isServiceDeleted}
        updateOrderStatus={(orderId, newStatus) => {
          dispatch(updateOrderStatusRequest(orderId, newStatus));
          setStatusModalOpen(false);
        }}
      />

      <OrdersModal
        isOpen={modal1}
        toggle={() => setModal1(!modal1)}
        orderData={selectedOrderData}
      />
    </React.Fragment>
  );
};

// Helper function for badge colors
const getStatusBadgeColor = (status) => {
  const colors = {
    pending: 'warning',
    approved: 'success',
    progress: 'info',
    completed: 'primary',
    cancelled_by_user: 'danger',
    cancelled_by_provider: 'danger',
    cancelled_by_admin: 'dark',
    refunded: 'secondary',
  };
  return colors[status] || 'info';
};

const getPaymentStatusColor = (status = "") => {
  const colors = { paid: "success", pending: "warning", failed: "danger", progress: "warning" };
  return colors[status] || "secondary";
};

const translatePaymentStatus = (status = "") => {
  const map = { paid: "تم الدفع", pending: "قيد الانتظار", failed: "فشل الدفع", progress: "قيد التنفيذ" };
  return map[status] || status || "غير محدد";
};

OrderManagementDashboard.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    booking_status: PropTypes.string.isRequired,
    app_user_id: PropTypes.object,
    service_providers_id: PropTypes.object,
    serviceProviderServices: PropTypes.object,
    create_at: PropTypes.object,
    // ... other order properties
  })).isRequired,
};

export default OrderManagementDashboard;

// ... keep the existing translateStatus and FilterOptions components

const translateStatus = status => {
  const translations = {
    pending: "قيد الانتظار",
    approved: "تم الموافقة عليه",
    progress: "قيد التنفيذ",
    completed: "تم الاكتمال",
    cancelled_by_user: "ألغي بواسطة العميل",
    cancelled_by_provider: "ألغي بواسطة مقدم الخدمة",
    cancelled_by_admin: "ألغي بواسطة الإدارة",
    refunded: "تم الاسترداد",
    cancelled: "ملغى",
    rejected: "مرفوض",
  };

  return translations[status] || status;
};

const FilterOptions = ({ filterValue, setFilterValue }) => {
  return (
    <div className="mb-4">
      <label>تصفية حسب الحالة:</label>
      <select
        className="form-control"
        id="filter"
        value={filterValue}
        onChange={e => setFilterValue(e.target.value)}
      >
        <option value="">الكل</option>
        <option value="delayed">الطلبات المتأخرة</option>
        <option value="pending">قيد الانتظار</option>
        <option value="approved">تم الموافقة</option>
        <option value="progress">قيد التنفيذ</option>
        <option value="completed">تم الاكتمال</option>
        <option value="cancelled_by_user">ألغي بواسطة العميل</option>
        <option value="cancelled_by_provider">ألغي بواسطة مقدم الخدمة</option>
        <option value="cancelled_by_admin">ألغي بواسطة الإدارة</option>
        <option value="refunded">تم الاسترداد</option>
      </select>
    </div>
  );
};